import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Select,
  FormControl,
  Autocomplete,
  TablePagination,
  TableSortLabel,
  Card,
} from "@mui/material";
import { FilterBar, FilterSearch } from "../ui/FilterBar";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import GenericModal from "../common/modal";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AssignProjectsDialog from "./AssignProjectsDialog";
import AddUserDialog from "./AddUserDialog"
import { GroupApi, UserApi, RoleApi } from "../../apis";
import { can } from "../../utils/auth";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Label } from "@mui/icons-material";
const customChipColors = [
  { bg: '#FF6B6B', color: '#fff' },  // Red
  { bg: '#4ECDC4', color: '#fff' },  // Teal
  { bg: '#45B7D1', color: '#fff' },  // Blue
  { bg: '#96CEB4', color: '#fff' },  // Sage
  { bg: '#FFBE0B', color: '#000' },  // Yellow
  { bg: '#9B5DE5', color: '#fff' },  // Purple
  { bg: '#F15BB5', color: '#fff' },  // Pink
];
/**
 * Mobile presentation for the same rows the table renders.
 *
 * A wide <Table> can't be made usable on a phone by scrolling it sideways, so
 * below `md` each row becomes a card: the first column is the title and the
 * rest are label/value pairs. Values come from the table's own
 * `renderCellValue`, so cells stay identical across both layouts.
 */
function MobileRowCards({ rows, columns, renderCellValue, showAction, renderActions, onRowClick }) {
  if (!rows?.length) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No data found
        </Typography>
      </Box>
    );
  }

  const [titleColumn, ...restColumns] = columns;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 1 }}>
      {rows.map((row, rowIndex) => (
        <Card
          key={rowIndex}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            p: 2,
            cursor: onRowClick ? "pointer" : "default",
            ...(onRowClick && {
              "&:hover": { boxShadow: (t) => t.shadows[4], borderColor: "primary.main" },
              "&:active": { transform: "scale(0.995)" },
            }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {renderCellValue(row, titleColumn)}
              </Typography>
            </Box>
            {showAction === true && (
              <Box
                sx={{ flexShrink: 0, mt: -0.5, mr: -1 }}
                onClick={(e) => e.stopPropagation()}
              >
                {renderActions(row)}
              </Box>
            )}
          </Box>

          {restColumns.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: 1,
                borderColor: "border.subtle",
                display: "grid",
                gridTemplateColumns: "minmax(80px, auto) 1fr",
                rowGap: 1,
                columnGap: 2,
                alignItems: "start",
              }}
            >
              {restColumns.map((column, colIndex) => (
                <React.Fragment key={colIndex}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {column.headerName}
                  </Typography>
                  <Box sx={{ fontSize: "0.875rem", textAlign: "right", minWidth: 0 }}>
                    {renderCellValue(row, column)}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Card>
      ))}
    </Box>
  );
}

// Define the ActionMenu here
function ActionMenu({
  onEdit,
  onDelete,
  onAssign,
  assign,
  onView,
  addUser,
  onAddUser,
  showTimeSheet = false,
  changePassword,
  onChangePassword
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            onEdit();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            onDelete();
          }}
        >
          Delete
        </MenuItem>
        {showTimeSheet && onView && (
          <MenuItem
            onClick={() => {
              handleClose();
              onView();
            }}
          >
            View Weekly Time Sheet
          </MenuItem>
        )}
        {assign && (
          <MenuItem
            onClick={() => {
              handleClose();
              onAssign();
            }}
          >
            Assign Projects
          </MenuItem>
        )}
        {addUser && (
          <MenuItem
            onClick={() => {
              handleClose();
              onAddUser();
            }}
          >
            Add User
          </MenuItem>
        )}
        {
          changePassword && (
            <MenuItem
              onClick={() => {
                onChangePassword();
              }}
            >
              Change Password
            </MenuItem>
          )
        }

      </Menu>
    </div>
  );
}

// Define the GenericTable component here
function GenericTable({
  data,
  columns,
  onEdit,
  onDelete,
  onChangePassword,
  assign = false,
  addUser = false,
  onAddUser,
  projects = [],
  fetchUsers,
  fetchAllGroups,
  onView,
  // Opens a row rather than acting on it; rows become clickable when set.
  onRowClick,
  type,
  showAction = true,
  showTimeSheet = false,
  is_group_selection_shown = false,
  isSearchShown = true,
  changePassword = false,
  customFilters,
  statusFilter,
  statusField,
  inlineEdit = true
}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [isAssignDialogOpen, setAssignDialogOpen] = useState(false); // Dialog state
  const [formData, setFormData] = useState({ phases: [""], groupIds: [""] }); // Initialize phases with an empty string
  const [selectedProjects, setSelectedProjects] = useState([]); // Selected projects state
  const [username, setUsername] = useState("");
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [groupId, setGroupId] = useState(null);
  // The roles a user may be moved to. Only the user table needs them, and
  // only for somebody who may edit a user at all.
  const [assignableRoles, setAssignableRoles] = useState([]);
  const fetchGroups = async () => {
    try {
      const response = await GroupApi.getAllGroups();
      const fetchedGroups = response.data.data.map((group) => ({
        id: group.id,
        name: group.name,
      }));
      setGroups(fetchedGroups);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };
  const [search, setSearch] = useState("");
  // Pagination state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Sorting state
  const [orderBy, setOrderBy] = useState("")
  const [order, setOrder] = useState("asc")
  
  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);
  
  // Filter data based on search input and status filter
  const filteredData = data.filter((row) => {
    // Apply search filter
    const matchesSearch = Object.values(row).some((value) => {
      if (value === null || value === undefined) {
        return false;
      }
      return value
        .toString()
        .toLowerCase()
        .includes(search.toLowerCase());
    });
    
    // Apply status filter if provided
    const matchesStatus = !statusFilter || statusFilter === "All" || !statusField
      ? true
      : row[statusField]?.toString().toLowerCase() === statusFilter.toString().toLowerCase();
    
    return matchesSearch && matchesStatus;
  });
  // Sort the filtered data
  // const sortedData = React.useMemo(() => {
  //   if (!orderBy) return filteredData

  //   return [...filteredData].sort((a, b) => {
  //     const aValue = a[orderBy]
  //     const bValue = b[orderBy]

  //     // Handle null or undefined values
  //     if (!aValue) return order === "asc" ? -1 : 1
  //     if (!bValue) return order === "asc" ? 1 : -1

  //     // Handle different data types
  //     if (typeof aValue === "string" && typeof bValue === "string") {
  //       return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
  //     }

  //     // For numbers and other types
  //     return order === "asc" ? aValue - bValue : bValue - aValue
  //   })
  // }, [filteredData, orderBy, order])
  // Sort the filtered data
  const sortedData = React.useMemo(() => {
    if (!orderBy) return filteredData

    return [...filteredData].sort((a, b) => {
      const aValue = a[orderBy]
      const bValue = b[orderBy]

      // Handle null or undefined values
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return order === "asc" ? -1 : 1
      if (bValue == null) return order === "asc" ? 1 : -1

      // Special handling for phases field (arrays of objects with name)
      if (orderBy === "phases") {
        const aStr = Array.isArray(aValue)
          ? aValue
              .map((p) => p?.name || "")
              .join("")
              .toLowerCase()
          : ""
        const bStr = Array.isArray(bValue)
          ? bValue
              .map((p) => p?.name || "")
              .join("")
              .toLowerCase()
          : ""
        return order === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
      }

      // Special handling for groups field (arrays of group objects)
      if (orderBy === "groups") {
        const aStr = Array.isArray(aValue)
          ? aValue
              .map((g) => g?.name || "")
              .join("")
              .toLowerCase()
          : ""
        const bStr = Array.isArray(bValue)
          ? bValue
              .map((g) => g?.name || "")
              .join("")
              .toLowerCase()
          : ""
        return order === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
      }

      // Handle different data types
      if (typeof aValue === "string" && typeof bValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
      }

      // For numbers and other comparable types
      const aNum = Number(aValue)
      const bNum = Number(bValue)

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return order === "asc" ? aNum - bNum : bNum - aNum
      }

      // Fallback: compare as strings
      const aStr = String(aValue)
      const bStr = String(bValue)
      return order === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
  }, [filteredData, orderBy, order])

  // Get current page data
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const getColor = (status) => {
    if (status == null || typeof status !== "string") {
      return "#FFEB3B";
    }
    const normalized = status.trim();
    if (!normalized) return "#FFEB3B";
    // convert to capital
    status = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    switch (status) {
      case "Active":
        return "#4CAF50";
      case "Inactive":
        return "#F44336";
      // Projects now end in Completed or Canceled rather than Inactive.
      case "Completed":
        return "#4CAF50";
      case "Canceled":
      case "Cancelled":
        return "#9E9E9E";
      // pending
      case "Pending":
        return "#FF9800";
      // Approved
      case "Approved":
        return "#4CAF50";
      case "Rejected":
        return "#F44336";
      default:
        return "#FFEB3B";
    }
  };
  useEffect(() => {
    // The group list only feeds the "assign to group" dialog, but this table
    // is shared by screens that have nothing to do with groups — so without
    // the check every one of them fired a request that could only 403.
    if (!can("groups.view")) return;
    fetchGroups();
  }, []);

  useEffect(() => {
    if (type !== "users" || !can("users.edit")) return;
    let cancelled = false;
    RoleApi.getAssignableRoles()
      .then((res) => {
        if (!cancelled) setAssignableRoles(res?.data?.data?.roles || []);
      })
      .catch((error) => {
        console.error("Error fetching assignable roles:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);
  const handleEdit = (row) => {
    if (!inlineEdit) {
      if (typeof onEdit === "function") {
        onEdit(row);
      }
      return;
    }
    setSelectedRow(row);
    const updatedPhases = Array.isArray(row.phases)
      ? row.phases.map((phase) =>
        typeof phase === "object" ? phase.name || "" : phase
      )
      : [""];
    const selectedGroupIds =
      row.groupId ||
      (row?.groupUser && row?.groupUser.length > 0
        ? row.groupUser.map((gu) => gu.groupId)
        : []);
    setFormData({ ...row, phases: updatedPhases, groupIds: selectedGroupIds });
    // setFormData({ ...row, phases: updatedPhases, groupId: row.groupId || [] });

    setEditModalOpen(true);
  };
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUsername(user.name);
  }, []);
  const handleAssign = async (row) => {
    setSelectedRow(row);
    try {
      if (type === "groups") {
        const response = await GroupApi.getProjectById(row.id);
        setSelectedProjects(response.data.data || []);
      } else if (type === "users") {
        const response = await UserApi.getAssignedProjects(row.id);
        setSelectedProjects(response.data.data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching assigned projects:", error);
    }
    setAssignDialogOpen(true);
  }


  const handleSave = async () => {
    try {
      if (selectedRow) {
        // Call the onEdit function passed as a prop with ID and updated data
        await onEdit({ ...formData, id: selectedRow.id });
        setEditModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating data:", error);
      // Handle error appropriately (e.g., show a notification or alert)
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhaseChange = (index, event) => {
    const newPhases = [...formData.phases];
    newPhases[index] = event.target.value;
    setFormData({
      ...formData,
      phases: newPhases,
    });
  };

  const addPhase = () => {
    setFormData({
      ...formData,
      phases: [...formData.phases, ""],
    });
  };

  const removePhase = (index) => {
    const newPhases = formData.phases.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      phases: newPhases,
    });
  };

  const handleProjectAssign = async (selectedProjects) => {
    const editData = {
      userId: selectedRow.id,
      projectIds: selectedProjects,
    };
    const projectIds = selectedProjects.map((project) => project.id);

    const editProject = {
      groupId: selectedRow.id,
      projectIds,
    }
    try {
      if (type === "groups") {
        await GroupApi.projectAssignToGroup(editProject);
      } else if (type === "users") {
        await UserApi.assignProjectToUser(editData);
      }
      fetchUsers();
    } catch (error) {
      console.error(
        "Error updating project:",
        error.response?.data?.message || error.message
      );
    } finally {
      setAssignDialogOpen(false);
    }
  };
  const handleAddUser = (row) => {
    setGroupId(row.id); // Store the selected row's ID
    setIsAddUserDialogOpen(true); // Open the Add User dialog
  };
  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }
  // render value for formatig change
  const renderCellValue = (row, column) => {
    const value = row[column.field]

    // If column has a renderCell function, use it
    if (column.renderCell) {
      try {
        return column.renderCell({
          value,
          row,
          field: column.field,
        })
      } catch (error) {
        console.error(`Error rendering cell for ${column.field}:`, error)
        return value || ""
      }
    }

    // If column has a valueFormatter function, use it
    if (column.valueFormatter && value) {
      try {
        // Try to apply the formatter
        return column.valueFormatter({ value })
      } catch (error) {
        // If formatting fails, return the original value
        console.error(`Error formatting value for ${column.field}:`, error)
        return value || ""
      }
    }

    // Handle special cases
    if (column.field === "userName") {
      return username || "N/A"
    } else if (column.field === "groupUser") {
      if (Array.isArray(row.groupUser) && row.groupUser.length > 0) {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.groupUser.map((groupUser, chipIndex) => {
              const userName = groupUser.user && groupUser.user.name ? groupUser.user.name : "Unknown"
              const chipColor = customChipColors[chipIndex % customChipColors.length]
              return (
                <Chip
                  key={groupUser.id}
                  label={userName}
                  size="small"
                  sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                />
              )
            })}
          </Box>
        )
      } else {
        return <Chip label={"No User"} size="small" sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }} />
      }
    } else if (column.field === "status") {
      return (
        <Chip
          label={value != null && value !== "" ? String(value) : "—"}
          sx={{
            backgroundColor: getColor(value),
            color: "#FFFFFF",
          }}
          size="small"
        />
      )
    } else if (column.field === "phases") {
      if (Array.isArray(value)) {
        return value
          .map((phase) => (phase.name ? phase.name.replace(/"/g, "") : ""))
          .filter((name) => name !== "")
          .join(", ")
      }
      return ""
    } else if (column.field === "projectDetails") {
      return (
        <div
          style={{
            whiteSpace: "pre-line",
            fontWeight: "bold",
            color: "#1976d2",
            fontSize: "14px",
          }}
        >
          {value?.split(", ").map((project, index) => (
            <div key={index}>• {project}</div>
          ))}
        </div>
      )
    } else if (column.field === "groups") {
      if (Array.isArray(row.groups) && row.groups.length > 0) {
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {row.groups.map((group, chipIndex) => {
              const chipColor = customChipColors[chipIndex % customChipColors.length]
              return (
                <Chip
                  key={group.id}
                  label={group.name}
                  size="small"
                  sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                />
              )
            })}
          </Box>
        )
      } else {
        return <Chip label={"No Groups"} size="small" sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }} />
      }
    } else if (column.field === "weekStart" || column.field === "weekEnd") {
      // Special handling for date fields
      if (value) {
        try {
          const dayjs = require("dayjs")
          return dayjs(value).format("DD-MM-YYYY")
        } catch (error) {
          console.error(`Error formatting date for ${column.field}:`, error)
          return value
        }
      }
      return ""
    }

    // Default case: return the value or empty string
    return value || ""
  }
  return (
    <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
      {/* MUI TextField for Search */}
      {
        isSearchShown && (
          <FilterBar sx={{ mb: 3, rowGap: 1.5 }}>
            <FilterSearch value={search} onChange={setSearch} placeholder="Search" />
            {customFilters}
          </FilterBar>
        )
      }

      <TableContainer sx={{ maxWidth: "100%" }}>
        {/* Phones get cards; the table below takes over from md up. */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <MobileRowCards
            onRowClick={onRowClick}
            rows={paginatedData}
            columns={columns}
            renderCellValue={renderCellValue}
            showAction={showAction}
            renderActions={(row) => (
              <ActionMenu
                type={type}
                assign={assign}
                showTimeSheet={showTimeSheet}
                onEdit={() => handleEdit(row)}
                onDelete={() => onDelete(row.id)}
                onAssign={() => handleAssign(row)}
                onView={() => onView(row.id)}
                onAddUser={() => handleAddUser(row)}
                addUser={addUser}
                changePassword={changePassword}
                onChangePassword={() => onChangePassword(row)}
              />
            )}
          />
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Table>
          <TableHead>
            {/* <TableRow>
              {columns.map((column, index) => (
                <TableCell key={index}>
                  {column.sortable !== false ? (
                    <Box
                      hideSortIcon={false} 
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : "asc"}
                      onClick={() => handleRequestSort(column.field)}
                      sx={{
                        display:"flex",
                        alignItems: "center",
                        color: 'inherit', // ensures the text color remains the same
                        '&:hover': {
                          color: 'inherit' // overrides any hover color changes
                        }
                      }}
                    >
                      {column.headerName}
                      <SwapVertIcon sx={{ ml: 1 ,fontSize: 16 }} />
                    </Box>
                  ) : (
                    column.headerName
                  )}
                </TableCell>
              ))}
              {showAction === true && <TableCell align="right">Actions</TableCell>}
            </TableRow> */}
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={index}>
                  {column.sortable === true ? (
                    <Box
                      sx={{ display: 'flex', alignItems: "center", cursor: 'pointer' }}
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : "asc"}
                      onClick={() => handleRequestSort(column.field)}
                    >
                      {column.headerName}  <SwapVertIcon sx={{ ml: 1, fontSize: 16 }} />
                    </Box>
                  ) : (
                    column.headerName
                  )}
                </TableCell>
              ))}
              {showAction === true && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          {/* <TableBody>
            {
              filteredData?.length > 0 ? (
                filteredData.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((column, colIndex) => (

                      <TableCell key={colIndex}>
                        {column.field === "userName" ? (
                          username || "N/A"
                        ) : column.field === "groupUser" ? (
                          Array.isArray(row.groupUser) && row.groupUser.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {row.groupUser.map((groupUser, chipIndex) => {
                                // Safely extract the user's name
                                const userName =
                                  groupUser.user && groupUser.user.name
                                    ? groupUser.user.name
                                    : "Unknown";
                                // Use the chipIndex (not the outer index) to cycle through the colors
                                const chipColor = customChipColors[chipIndex % customChipColors.length];
                                return (
                                  <Chip
                                    key={groupUser.id}
                                    label={userName}
                                    size="small"
                                    sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                                  />
                                );
                              })}
                            </Box>
                          ) : (
                            <Chip
                              // key={groupUser.id}
                              label={"No User"}
                              size="small"
                              sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }}
                            />
                          )
                        ) : column.field === "status" ? (
                          <Chip label={row[column.field]}
                            sx={{
                              backgroundColor: getColor(row[column.field]),
                              color: "#FFFFFF",
                            }}
                            size="small" />
                        ) : column.field === "phases" ? (
                          Array.isArray(row[column.field]) ? (
                            row[column.field]
                              .map((phase) =>
                                phase.name ? phase.name.replace(/"/g, "") : ""
                              )
                              .filter((name) => name !== "")
                              .join(", ")
                          ) : (
                            ""
                          )
                        ) :
                          column.field === "projectDetails" ? (
                            <div style={{
                              whiteSpace: "pre-line",
                              fontWeight: "bold",
                              color: "#1976d2",
                              fontSize: "14px"
                            }}>
                              {row[column.field].split(", ").map((project, index) => (
                                <div key={index}>• {project}</div>
                              ))}
                            </div>
                          ) :
                            column.field === "groups" ? (
                              //  in chips
                              Array.isArray(row.groups) && row.groups.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {row.groups.map((group, chipIndex) => {
                                    // Use the chipIndex (not the outer index) to cycle through the colors
                                    const chipColor = customChipColors[chipIndex % customChipColors.length];
                                    return (
                                      <Chip
                                        key={group.id}
                                        label={group.name}
                                        size="small"
                                        sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                                      />
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Chip
                                  // key={groupUser.id}
                                  label={"No Groups"}
                                  size="small"
                                  sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }}
                                />
                              )
                            ) :
                              (
                                row[column.field] || ""
                              )
                        }
                      </TableCell>
                    ))}
                    {showAction == true && (
                      <TableCell align="right" showAction={true}>
                        <ActionMenu
                          type={type}
                          assign={assign}
                          showTimeSheet={showTimeSheet}
                          onEdit={() => handleEdit(row)}
                          onDelete={() => onDelete(row.id)}
                          onAssign={() => handleAssign(row)}
                          onView={() => onView(row.id)}
                          onAddUser={() => handleAddUser(row)}
                          addUser={addUser}
                          changePassword={changePassword}
                          onChangePassword={() => onChangePassword(row)}
                        />
                      </TableCell>
                    )}

                  </TableRow>
                )))
                :
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    No data found
                  </TableCell>
                </TableRow>

            }
          </TableBody> */}
          {/* <TableBody>
            {paginatedData?.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex}>
                      {column.field === "userName" ? (
                        username || "N/A"
                      ) : column.field === "groupUser" ? (
                        Array.isArray(row.groupUser) && row.groupUser.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {row.groupUser.map((groupUser, chipIndex) => {
                              // Safely extract the user's name
                              const userName = groupUser.user && groupUser.user.name ? groupUser.user.name : "Unknown"
                              // Use the chipIndex (not the outer index) to cycle through the colors
                              const chipColor = customChipColors[chipIndex % customChipColors.length]
                              return (
                                <Chip
                                  key={groupUser.id}
                                  label={userName}
                                  size="small"
                                  sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                                />
                              )
                            })}
                          </Box>
                        ) : (
                          <Chip label={"No User"} size="small" sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }} />
                        )
                      ) : column.field === "status" ? (
                        <Chip
                          label={row[column.field]}
                          sx={{
                            backgroundColor: getColor(row[column.field]),
                            color: "#FFFFFF",
                          }}
                          size="small"
                        />
                      ) : column.field === "phases" ? (
                        Array.isArray(row[column.field]) ? (
                          row[column.field]
                            .map((phase) => (phase.name ? phase.name.replace(/"/g, "") : ""))
                            .filter((name) => name !== "")
                            .join(", ")
                        ) : (
                          ""
                        )
                      ) : column.field === "projectDetails" ? (
                        <div
                          style={{
                            whiteSpace: "pre-line",
                            fontWeight: "bold",
                            color: "#1976d2",
                            fontSize: "14px",
                          }}
                        >
                          {row[column.field]?.split(", ").map((project, index) => (
                            <div key={index}>• {project}</div>
                          ))}
                        </div>
                      ) : column.field === "groups" ? (
                        //  in chips
                        Array.isArray(row.groups) && row.groups.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {row.groups.map((group, chipIndex) => {
                              // Use the chipIndex (not the outer index) to cycle through the colors
                              const chipColor = customChipColors[chipIndex % customChipColors.length]
                              return (
                                <Chip
                                  key={group.id}
                                  label={group.name}
                                  size="small"
                                  sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                                />
                              )
                            })}
                          </Box>
                        ) : (
                          <Chip
                            label={"No Groups"}
                            size="small"
                            sx={{ backgroundColor: "#F1F4FA", color: "#011D38" }}
                          />
                        )
                      ) : (
                        row[column.field] || ""
                      )}
                    </TableCell>
                  ))}
                  {showAction === true && (
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <ActionMenu
                        type={type}
                        assign={assign}
                        showTimeSheet={showTimeSheet}
                        onEdit={() => handleEdit(row)}
                        onDelete={() => onDelete(row.id)}
                        onAssign={() => handleAssign(row)}
                        onView={() => onView(row.id)}
                        onAddUser={() => handleAddUser(row)}
                        addUser={addUser}
                        changePassword={changePassword}
                        onChangePassword={() => onChangePassword(row)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody> */}
           <TableBody>
            { paginatedData?.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  hover={Boolean(onRowClick)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex}>{renderCellValue(row, column)}</TableCell>
                  ))}
                  {showAction === true && (
                    <TableCell align="right">
                      <ActionMenu
                        type={type}
                        assign={assign}
                        showTimeSheet={showTimeSheet}
                        onEdit={() => handleEdit(row)}
                        onDelete={() => onDelete(row.id)}
                        onAssign={() => handleAssign(row)}
                        onView={() => onView(row.id)}
                        onAddUser={() => handleAddUser(row)}
                        addUser={addUser}
                        changePassword={changePassword}
                        onChangePassword={() => onChangePassword(row)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + (showAction ? 1 : 0)} align="center">
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </Box>
        {/* Pagination component */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <GenericModal
          open={isEditModalOpen}
          handleClose={() => setEditModalOpen(false)}
          title="Edit Data"
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {columns
              .filter((column) => column.field !== "id" && column.field !== "groupUser") // Exclude the 'id' field
              .map((column, index) => (
                <Box key={index} mb={2}>
                  <Typography variant="subtitle1">{column.headerName}</Typography>
                  {column.field === "roleName" ? (
                    <TextField
                      placeholder={column.headerName}
                      name={column.field}
                      value={formData[column.field] || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      select
                      helperText={
                        assignableRoles.find((role) => role.name === formData[column.field])
                          ?.description ||
                        "What each role can do is set under Roles & Permissions."
                      }
                    >
                      {/* The current role is listed even when it is not
                          assignable — an Admin viewed by a non-admin, say —
                          so opening the form cannot silently blank it. */}
                      {!assignableRoles.some((role) => role.name === formData[column.field]) &&
                        formData[column.field] && (
                          <MenuItem value={formData[column.field]}>
                            {formData[column.field]}
                          </MenuItem>
                        )}
                      {assignableRoles.map((role) => (
                        <MenuItem key={role.id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : column.field === "status" ? (
                    <TextField
                      placeholder={column.headerName}
                      name={column.field}
                      value={formData[column.field] || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      select
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Inactive">Inactive</MenuItem>
                    </TextField>
                  ) : column.field === "phases" ? (
                    <>
                      {formData.phases.map((phase, idx) => (
                        <Box display="flex" alignItems="center" key={idx} mb={2}>
                          <TextField
                            placeholder="Phase"
                            name={`phases[${idx}]`}
                            value={phase.replace(/"/g, "")} // Remove quotation marks from the phase value
                            onChange={(event) => handlePhaseChange(idx, event)}
                            variant="outlined"
                            fullWidth
                            sx={{ mr: 1 }}
                          />
                          <IconButton onClick={() => removePhase(idx)} aria-label="remove phase">
                            <RemoveCircleIcon color="error" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={addPhase}
                        sx={{ mb: 2 }}
                        startIcon={<AddCircleIcon />}
                      >
                        Add Phase
                      </Button>
                    </>
                  )
                    : column.field === "groups" ? (
                      ""
                    ) :
                      (
                        <TextField
                          placeholder={column.headerName}
                          name={column.field}
                          value={formData[column.field] || ""}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                </Box>
              ))}
            {is_group_selection_shown && (
              <>
                <Box mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Group(s)
                  </Typography>
                  <FormControl fullWidth>
                    <Autocomplete
                      multiple
                      options={groups}
                      getOptionLabel={(option) => option.name}
                      value={groups?.filter((group) =>
                        formData.groups?.map((group) => group.id).includes(group.id)
                      )}
                      onChange={(event, newValue) => {
                        setFormData({
                          ...formData,
                          groupIds: newValue.map((group) => group.id), // Changed from `groupIds` to `groups` for consistency
                          groups: newValue.map((group) => {
                            return { id: group.id, name: group.name }
                          }), // Changed from `groupIds` to `groups` for consistency
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select group(s)" variant="outlined" />
                      )}
                    />
                  </FormControl>
                </Box>

                {formData?.groups?.length > 0 && (
                  <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {formData.groups.map((groupId) => {
                      const group = groups.find((g) => g.id === groupId?.id);
                      return (
                        <Chip
                          key={groupId}
                          label={group?.name || ``}
                        />
                      );
                    })}
                  </Box>
                )}
              </>
            )}


            <Button onClick={handleSave} variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </GenericModal>


        <AssignProjectsDialog
          type={type}
          open={isAssignDialogOpen}
          handleClose={() => setAssignDialogOpen(false)}
          projects={projects}
          selectedProjects={selectedProjects}
          onAssign={handleProjectAssign}
        />
        <AddUserDialog
          open={isAddUserDialogOpen}
          handleClose={() => {
            setIsAddUserDialogOpen(false);
            fetchGroups();
            fetchAllGroups();

          }}
          onAdd={handleAddUser}
          groupId={groupId}
        />
      </TableContainer>
    </Paper>
  );
}

export default GenericTable;
